<script lang="ts" setup>
import type { MaxBid, Price } from "mws-ts-v2-sdk";
import { getFormattedCurrencyAmount } from "mws-utils/lib";

type Props = {
  isOutbid: boolean;
  isExtended: boolean;
  activeMaxBid?: MaxBid | null;
  endDate: Date;
  prices: Price[];
};

const { activeMaxBid, endDate, prices, isOutbid } = defineProps<Props>();

type Emits = {
  (e: "close"): void;
};

defineEmits<Emits>();

const { locale, t } = useI18n();
useDisableToast();

const { activeCurrency } = useActiveCurrency();

const formattedBidPrice = computed(() => {
  const priceInCurrency = getPriceInCurrency(
    prices,
    activeCurrency.value.isoCurrencyCode,
  );

  return getFormattedCurrencyAmount(
    locale.value,
    activeCurrency.value.isoCurrencyCode,
    priceInCurrency * 100,
  );
});

const formattedMaxBidPrice = computed(() => {
  if (!activeMaxBid?.prices) return;

  const priceInCurrency = getPriceInCurrency(
    activeMaxBid.prices,
    activeCurrency.value.isoCurrencyCode,
  );

  return getFormattedCurrencyAmount(
    locale.value,
    activeCurrency.value.isoCurrencyCode,
    priceInCurrency * 100,
  );
});

const { localizedDateTime: formattedEndTime } = useLocalizedDateTime(
  () => endDate,
  "time",
);

const modalContent = computed(() => {
  if (isOutbid) {
    return t("product.bidPlaced.outbid.description");
  }

  if (activeMaxBid) {
    return t("product.bidPlaced.success.maxBidDescription", {
      PLACEHOLDER_amount: formattedMaxBidPrice.value,
    });
  }

  return t("product.bidPlaced.success.description", {
    PLACEHOLDER_amount: formattedBidPrice.value,
  });
});
</script>

<template>
  <BaseDialog
    :model-value="true"
    @close="$emit('close')"
  >
    <BaseDialogContent>
      <template #title>
        {{
          isOutbid
            ? t("product.bidPlaced.outbid.title")
            : t("product.bidPlaced.success.title")
        }}
      </template>
      <template #content>
        <p>{{ modalContent }}</p>
        <I18nT
          v-if="isExtended"
          scope="global"
          tag="p"
          keypath="product.bidPlaced.auctionExtended"
          class="mt-2"
        >
          <template #newEndTime>
            <span class="font-semibold">
              {{ formattedEndTime }}
            </span>
          </template>
        </I18nT>
      </template>

      <template #buttons>
        <BaseButton
          data-cy="close-bid-modal-placed"
          @click="$emit('close')"
        >
          {{ $t("common.ok") }}
        </BaseButton>
      </template>
    </BaseDialogContent>
  </BaseDialog>
</template>
